import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, SealCheck, CalendarCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { E, EImg } from "@/components/Editable";

export const ExpertsSection = () => {
  const [readers, setReaders] = useState([]);

  useEffect(() => {
    api.get("/readers").then(({ data }) => setReaders(data)).catch(() => {});
  }, []);

  if (!readers.length) return null;

  return (
    <section className="relative py-24 lg:py-32 px-6 lg:px-10 max-w-7xl mx-auto" id="experts" data-testid="experts-section">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-4"><E k="experts_overline" f="Your guides" /></p>
        <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
          <E k="experts_heading" f="Expert Tarot & Numerology readers" />
        </h2>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          <E k="experts_subtext" f="Certified, practising professionals — 28+ combined years, 22,000+ readings delivered, every session private and judgement-free." />
        </p>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {readers.map((r, i) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.1 }}
            className="hover-lift bg-card border border-white/10 p-8 text-center"
            data-testid={`expert-card-${r.id}`}
          >
            <EImg
              k={`reader_${r.id}_image`}
              f={r.image}
              alt={r.name}
              wrapClassName="inline-block"
              className="w-24 h-24 rounded-full object-cover border-2 border-gold/50 mx-auto"
            />
            <h3 className="mt-5 font-heading text-2xl font-semibold tracking-tight flex items-center justify-center gap-1.5">
              <E k={`reader_${r.id}_name`} f={r.name} /> <SealCheck size={18} weight="fill" className="text-gold shrink-0" />
            </h3>
            <p className="font-heading italic text-sm text-muted-foreground mt-1"><E k={`reader_${r.id}_title`} f={r.title} /></p>
            <span className="inline-block mt-3 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 border border-gold/40 text-gold">
              <E k={`reader_${r.id}_specialty_label`} f={r.specialty_label} />
            </span>
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span>{r.experience_years}+ yrs</span>
              <span>{r.readings_done.toLocaleString()}+ readings</span>
              <span className="flex items-center gap-1 text-gold">
                <Star size={12} weight="fill" /> {Number(r.rating).toFixed(1)}
              </span>
            </div>
            <Link
              to="/book"
              data-testid={`expert-book-${r.id}`}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-gold text-black text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
            >
              <CalendarCheck size={14} weight="bold" /> Book a Session
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
